/**
 * The column vocabulary every tracker board shares: what types a column can be,
 * what each one is called and how narrow it may be dragged.
 *
 * It lives apart from any one board's engine because three of them now read it
 * — the briefs board, the free-form board and the record-backed board — and a
 * type added in one of their files would be invisible to the other two.
 */
import type { ParseKeys } from 'i18next';

import { i18n } from '@/shared/i18n';

/**
 * Built-in reorderable columns. Currently empty — every reorderable column
 * is user-defined via the Add Column flow. The map remains so consumers can
 * uniformly look up `columnDefs[id]` for either a future built-in or a
 * custom column.
 */
export const REORDERABLE_COLUMNS: Record<string, { label: string; minWidth: number }> = {};

export const DEFAULT_COLUMN_ORDER: string[] = [];

export const ITEM_COLUMN_DEFAULT_WIDTH = 250;
export const ITEM_COLUMN_MIN_WIDTH = 200;

/** Fixed Comments column width (no header, just the badge per row). */
export const COMMENTS_COLUMN_WIDTH = 52;

/**
 * Column types, mirroring the backend `CustomColumnType` enum with one
 * addition: `tags`, which no user can create.
 *
 * A record field picks its renderer out of this union too, and a project's tags
 * are a list of free strings rather than one. Keeping it out of the Add-column
 * catalogue is what stops it reaching the backend, so the enums stay in step
 * even though the union is one longer.
 */
export type CustomColumnType =
  | 'text'
  | 'tags'
  | 'number'
  | 'date'
  | 'status'
  | 'brief_type'
  | 'project'
  | 'people'
  | 'assets'
  | 'checkbox'
  | 'date_range'
  | 'link'
  | 'button'
  | 'timeline'
  | 'team'
  | 'progress'
  | 'formula'
  | 'files'
  | 'approval';

/**
 * The type a column is saved under. `tags` is a renderer the board owns and the
 * backend enum has no room for, so it is narrowed away at the persist boundary
 * rather than 400ing the layout PATCH. Nothing should ever reach here as one:
 * no Add-column flow offers `tags` and a record field is never written into
 * `layout.customColumns`. The value of the function is that the compiler checks
 * that, at the one place it could stop being true.
 */
export function storedColumnType(type: CustomColumnType): Exclude<CustomColumnType, 'tags'> {
  return type === 'tags' ? 'text' : type;
}

/** A single selectable option for a status column. */
export interface StatusOption {
  id: string;
  label: string;
  /** Hex colour (e.g. `#f59e0b`). */
  color: string;
}

/**
 * Default palette new Status columns ship with. A factory, not a const: the
 * labels are seeded into the user's own (persisted, editable) layout, so they
 * must resolve in the language active when the column is created rather than
 * whichever one was active at import.
 */
export const defaultStatusOptions = (): StatusOption[] => [
  {
    id: 'working_on_it',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.workingOnIt'),
    color: '#fdab3d',
  },
  { id: 'done', label: i18n.t('trackers:trackersBoard.useTrackersBoard.done'), color: '#00c875' },
  { id: 'stuck', label: i18n.t('trackers:trackersBoard.useTrackersBoard.stuck'), color: '#e2445c' },
];

/**
 * Every colour a status chip on this board may take.
 *
 * Named rather than positional so code that needs one specific hue - a platform
 * status mapping its own vocabulary onto the board's - can say which, instead of
 * writing the hex again. A second copy of a hex escapes the contrast test in
 * `status-chip-colors.test.ts`, which walks this table.
 */
export const STATUS_COLORS = {
  slate: '#94a3b8',
  slateDark: '#64748b',
  red: '#e2445c',
  redBright: '#ef4444',
  orange: '#f97316',
  amber: '#fdab3d',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#00c875',
  cyan: '#06b6d4',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  fuchsia: '#d946ef',
  pink: '#ec4899',
} as const;

/** Fixed palette users pick from when configuring a status option's colour. */
export const STATUS_COLOR_PALETTE: string[] = Object.values(STATUS_COLORS);

export interface CustomColumn {
  id: string;
  label: string;
  type: CustomColumnType;
  /** Status-only: list of selectable options. Undefined for other types. */
  statusOptions?: StatusOption[];
  /** Button-only: the label rendered inside the button. Undefined for others. */
  buttonCaption?: string;
  /** Formula-only: the expression evaluated against the row's other columns. */
  formulaExpression?: string;
  /**
   * Whether the cell offers Clear. False for a platform field the record
   * cannot hold empty, where clearing is a write the server rejects.
   * Undefined means clearable, which is every column the board itself owns.
   */
  clearable?: boolean;
}

/** Default label key for a column of each type, used when the user names none. */
export const COLUMN_TYPE_LABEL_KEY: Record<CustomColumnType, ParseKeys<'trackers'>> = {
  approval: 'trackersBoard.useTrackersBoard.approval',
  assets: 'trackersBoard.useTrackersBoard.projectAssets',
  brief_type: 'trackersBoard.useTrackersBoard.briefType',
  button: 'trackersBoard.useTrackersBoard.button',
  checkbox: 'trackersBoard.useTrackersBoard.checkbox',
  date: 'trackersBoard.useTrackersBoard.date',
  date_range: 'trackersBoard.useTrackersBoard.dateRange',
  files: 'trackersBoard.useTrackersBoard.files',
  formula: 'trackersBoard.useTrackersBoard.formula',
  link: 'trackersBoard.useTrackersBoard.link',
  number: 'trackersBoard.useTrackersBoard.number',
  people: 'trackersBoard.useTrackersBoard.people',
  progress: 'trackersBoard.useTrackersBoard.progress',
  project: 'trackersBoard.useTrackersBoard.project',
  status: 'trackersBoard.useTrackersBoard.status',
  tags: 'trackersBoard.useTrackersBoard.tags',
  team: 'trackersBoard.useTrackersBoard.team',
  text: 'trackersBoard.useTrackersBoard.text',
  timeline: 'trackersBoard.useTrackersBoard.timeline',
};

/** Default width for newly-created custom columns. */
export const CUSTOM_COLUMN_DEFAULT_WIDTH = 140;

/**
 * Per-type minimum widths. The resize handle and `setColumnWidth` floor
 * incoming widths to these values so users can't shrink a column so far
 * that its content spills past the cell border. Tuned to comfortably fit
 * each type's natural rendered content (longest brief-type label, full
 * ISO date string, etc.).
 */
export const CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE: Record<CustomColumnType, number> = {
  text: 120,
  tags: 160,
  number: 80,
  date: 140,
  status: 120,
  brief_type: 150,
  project: 150,
  people: 100,
  assets: 200,
  checkbox: 70,
  date_range: 220,
  link: 140,
  button: 120,
  timeline: 220,
  team: 120,
  progress: 120,
  formula: 100,
  files: 120,
  approval: 130,
};

/** Fallback when the column's type is missing or unknown. */
export const CUSTOM_COLUMN_MIN_WIDTH = 100;

/**
 * Id for a column the user has just created. Generated client-side so the
 * column can ride the next layout PATCH without a round trip.
 *
 * Call it ONCE per new column, outside every state updater: StrictMode
 * double-invokes updaters in dev, so an id minted inside one lands twice in
 * `columnOrder` while `customColumns` gets a single entry — an orphan id with
 * no column definition, which crashes the header/row map.
 */
export function newColumnId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();

  return `cc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * `base`, or the first of its numbered variants nothing else on the board
 * answers to. Column headers are how a formula names its operands, so two
 * columns sharing one would make the reference ambiguous.
 *
 * `numbered` builds the nth variant, because "Status 2" and "Copy of Status 2"
 * are different sentences and each is a translated string of its own.
 */
export function uniqueLabel(
  base: string,
  taken: Iterable<string>,

  numbered: (n: number) => string = (n) => `${base} ${n}`,
): string {
  const existing = new Set(taken);
  let label = base;
  for (let n = 2; existing.has(label); n += 1) label = numbered(n);
  return label;
}
